import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, PhoneIncoming, PhoneOutgoing } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import {
  reportsApi,
  type CdrItem,
  type ReportBucket,
  type ReportDirection,
} from "../api/reportsApi";
import { numbersApi, type NumberListItem } from "../api/numbersApi";
import { formatPhoneNumber } from "../lib/phone";
import { ErrorTile, StatusChip, Td, Th, fmtDay, fmtDuration, fmtMonth, fmtTime, nowInTz, tzLabel } from "../components/reports/ReportBits";
import { ScrollShadow } from "../components/ui/ScrollShadow";

/**
 * Inbound / Outbound call report with a three-level drill-down driven by
 * the route:
 *   /reports/:direction                → per day (one month, prev/next)
 *   /reports/:direction/:date          → per hour of that day
 *   /reports/:direction/:date/:hour    → the calls (CDRs) in that hour
 * An optional `?dnisId=` narrows every level to a single number and is
 * carried along while drilling. Dates and times are in the customer's report time zone.
 */
export function CallReportPage() {
  const params = useParams<{ direction: string; date?: string; hour?: string }>();
  const [search, setSearch] = useSearchParams();
  const direction = params.direction === "inbound" || params.direction === "outbound" ? params.direction : null;
  const dnisId = Number(search.get("dnisId")) || null;
  const keepQuery = dnisId ? `?dnisId=${dnisId}` : "";
  // Customer numbers for the per-number filter (fetched once).
  const [numbers, setNumbers] = useState<NumberListItem[]>([]);
  useEffect(() => {
    let active = true;
    numbersApi.list().then((r) => active && setNumbers(r.items ?? [])).catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);
  const selectNumber = (value: string) => {
    const next = new URLSearchParams(search);
    if (value) next.set("dnisId", value); else next.delete("dnisId");
    setSearch(next);
  };

  if (!direction) {
    return (
      <>
        <PageHeader title="Reports" />
        <div className="dashboard">
          <div className="tile" style={{ color: "var(--portal-muted)" }}>Unknown report.</div>
        </div>
      </>
    );
  }

  const title = direction === "inbound" ? "Inbound calls" : "Outbound calls";
  const Icon = direction === "inbound" ? PhoneIncoming : PhoneOutgoing;
  const base = `/reports/${direction}`;

  const crumbs: { label: string; to?: string }[] = [{ label: title, to: params.date ? base + keepQuery : undefined }];
  if (params.date) crumbs.push({ label: fmtDay(params.date), to: params.hour != null ? `${base}/${params.date}${keepQuery}` : undefined });
  if (params.hour != null) crumbs.push({ label: `${params.hour.padStart(2, "0")}:00 – ${String((Number(params.hour) + 1) % 24).padStart(2, "0")}:00 ${tzLabel()}` });

  return (
    <>
      <PageHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            <Icon size={22} aria-hidden="true" /> {title}
          </span>
        }
      />
      <div className="dashboard">
        <nav aria-label="Breadcrumb" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.9rem", flexWrap: "wrap", margin: "4px 0 12px" }}>
          {crumbs.map((c, i) => (
            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {i > 0 && <ChevronRight size={14} style={{ color: "var(--portal-muted)" }} />}
              {c.to ? <Link to={c.to}>{c.label}</Link> : <strong>{c.label}</strong>}
            </span>
          ))}
          <label style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "var(--portal-muted)" }}>Number</span>
            <select
              className="portal-input-inline"
              value={dnisId ?? ""}
              onChange={(e) => selectNumber(e.target.value)}
              style={{ padding: "6px 10px", border: "1px solid var(--portal-border)", borderRadius: 6, fontFamily: "inherit", fontSize: "0.9rem", background: "white", minWidth: 180 }}
            >
              <option value="">All numbers</option>
              {numbers.map((n) => (
                <option key={n.dnisId} value={n.dnisId}>
                  {formatPhoneNumber(n.number, n.countryCode, n.areaCode) || n.number}
                </option>
              ))}
            </select>
          </label>
        </nav>

        {params.hour != null && params.date ? (
          <CdrsView direction={direction} date={params.date} hour={Number(params.hour)} dnisId={dnisId} />
        ) : params.date ? (
          <HoursView direction={direction} date={params.date} dnisId={dnisId} base={base} keepQuery={keepQuery} />
        ) : (
          <DaysView direction={direction} dnisId={dnisId} base={base} keepQuery={keepQuery} />
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Level 1: days of a month
// ---------------------------------------------------------------------------

function DaysView({ direction, dnisId, base, keepQuery }: { direction: ReportDirection; dnisId: number | null; base: string; keepQuery: string }) {
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const now = new Date();
  const tzNow = nowInTz();
  const year = Number(search.get("y")) || tzNow.year;
  const month = Number(search.get("m")) || tzNow.month;
  const [rows, setRows] = useState<ReportBucket[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setRows(null);
    setError(null);
    reportsApi
      .daily(direction, year, month, dnisId)
      .then((r) => active && setRows(r.days))
      .catch((e) => active && setError(e instanceof Error ? e.message : "Failed to load report"));
    return () => {
      active = false;
    };
  }, [direction, year, month, dnisId]);

  const shift = (delta: number) => {
    const d = new Date(Date.UTC(year, month - 1 + delta, 1));
    const next = new URLSearchParams(search);
    next.set("y", String(d.getUTCFullYear()));
    next.set("m", String(d.getUTCMonth() + 1));
    setSearch(next);
  };
  const isCurrentMonth = year === tzNow.year && month === tzNow.month;
  const todayKey = now.toISOString().slice(0, 10);
  const visible = rows?.filter((r) => r.key <= todayKey) ?? [];
  const totals = useMemo(() => sum(visible), [visible]);

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button type="button" className="btn btn-secondary" onClick={() => shift(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <ChevronLeft size={14} /> Prev
        </button>
        <strong style={{ minWidth: 150, textAlign: "center" }}>{fmtMonth(year, month)}</strong>
        <button type="button" className="btn btn-secondary" onClick={() => shift(1)} disabled={isCurrentMonth} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          Next <ChevronRight size={14} />
        </button>
      </div>

      {error && <ErrorTile message={error} />}
      {!rows && !error && <div className="tile" style={{ color: "var(--portal-muted)" }}>Loading…</div>}
      {rows && (
        <BucketTable
          firstHeader="Day"
          rows={visible}
          totals={totals}
          labelOf={(r) => fmtDay(r.key)}
          onOpen={(r) => navigate(`${base}/${r.key}${keepQuery}`)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Level 2: hours of a day
// ---------------------------------------------------------------------------

function HoursView({ direction, date, dnisId, base, keepQuery }: { direction: ReportDirection; date: string; dnisId: number | null; base: string; keepQuery: string }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ReportBucket[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setRows(null);
    setError(null);
    reportsApi
      .hourly(direction, date, dnisId)
      .then((r) => active && setRows(r.hours))
      .catch((e) => active && setError(e instanceof Error ? e.message : "Failed to load report"));
    return () => {
      active = false;
    };
  }, [direction, date, dnisId]);

  const totals = useMemo(() => sum(rows ?? []), [rows]);

  return (
    <>
      {error && <ErrorTile message={error} />}
      {!rows && !error && <div className="tile" style={{ color: "var(--portal-muted)" }}>Loading…</div>}
      {rows && (
        <BucketTable
          firstHeader={`Hour (${tzLabel()})`}
          rows={rows}
          totals={totals}
          labelOf={(r) => `${r.key}:00 – ${String((Number(r.key) + 1) % 24).padStart(2, "0")}:00`}
          onOpen={(r) => navigate(`${base}/${date}/${Number(r.key)}${keepQuery}`)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Level 3: calls in an hour
// ---------------------------------------------------------------------------

function CdrsView({ direction, date, hour, dnisId }: { direction: ReportDirection; date: string; hour: number; dnisId: number | null }) {
  const [items, setItems] = useState<CdrItem[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const showCause = direction === "outbound";

  useEffect(() => {
    let active = true;
    setItems(null);
    setError(null);
    reportsApi
      .cdrs(direction, date, hour, dnisId)
      .then((r) => {
        if (!active) return;
        setItems(r.items);
        setTruncated(r.truncated);
      })
      .catch((e) => active && setError(e instanceof Error ? e.message : "Failed to load calls"));
    return () => {
      active = false;
    };
  }, [direction, date, hour, dnisId]);

  return (
    <>
      {error && <ErrorTile message={error} />}
      {!items && !error && <div className="tile" style={{ color: "var(--portal-muted)" }}>Loading…</div>}
      {items && (
        <section className="tile" style={{ padding: 0 }}>
          <ScrollShadow><table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.9rem" }}>
            <thead>
              <tr>
                <Th>Start ({tzLabel()})</Th>
                <Th>{direction === "inbound" ? "Calling number" : "Called number"}</Th>
                <Th>Number</Th>
                <Th align="right">Ring time</Th>
                <Th align="right">Talk time</Th>
                <Th>Status</Th>
                {showCause && <Th>Hangup cause</Th>}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={showCause ? 7 : 6} style={{ padding: 16, color: "var(--portal-muted)", textAlign: "center" }}>No calls in this hour.</td></tr>
              )}
              {items.map((c) => {
                const answered = c.acceptUtc != null;
                return (
                  <tr key={c.cdrId} style={c.isCredit ? { color: "var(--portal-muted)" } : undefined}>
                    <Td>{fmtTime(c.inviteUtc)}</Td>
                    <Td>
                      {(direction === "inbound" ? c.callingNumber : c.calledNumber) || "—"}
                      {c.isCredit && (
                        <span
                          title={`Continuation of call ${c.originalCallId ?? ""} after a credit top-up — not counted in the totals`}
                          style={{ marginLeft: 8, padding: "1px 7px", borderRadius: 999, border: "1px solid var(--portal-border)", background: "var(--portal-surface)", fontSize: "0.7rem", fontWeight: 600, verticalAlign: "middle" }}
                        >
                          Credit
                        </span>
                      )}
                    </Td>
                    <Td>{c.dnisPresentation || "—"}</Td>
                    <Td align="right">{c.ringSeconds != null ? fmtDuration(c.ringSeconds) : "—"}</Td>
                    <Td align="right">{answered ? fmtDuration(c.seconds ?? 0) : "—"}</Td>
                    <Td><StatusChip ok={answered} label={answered ? "Answered" : "Not answered"} /></Td>
                    {showCause && (
                      <Td>
                        <code title={c.hangupCause !== c.hangupCauseNormalised ? `raw: ${c.hangupCause || "(empty)"}` : undefined} style={{ fontSize: "0.8rem" }}>
                          {c.hangupCauseNormalised || "—"}
                        </code>
                      </Td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table></ScrollShadow>
          {truncated && (
            <div style={{ padding: "8px 12px", fontSize: "0.8rem", color: "var(--portal-muted)", borderTop: "1px solid var(--portal-border)" }}>
              Showing the first {items.length} calls of this hour; narrow down by number to see the rest.
            </div>
          )}
        </section>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function BucketTable({ firstHeader, rows, totals, labelOf, onOpen }: {
  firstHeader: string;
  rows: ReportBucket[];
  totals: ReportBucket;
  labelOf: (r: ReportBucket) => string;
  onOpen: (r: ReportBucket) => void;
}) {
  return (
    <section className="tile" style={{ padding: 0 }}>
      <ScrollShadow><table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.9rem" }}>
        <thead>
          <tr>
            <Th>{firstHeader}</Th>
            <Th align="right">Calls</Th>
            <Th align="right">Minutes</Th>
            <Th align="right">Avg. duration</Th>
            <Th align="right">Not answered</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const empty = r.calls === 0;
            return (
              <tr
                key={r.key}
                onClick={() => !empty && onOpen(r)}
                style={{ cursor: empty ? "default" : "pointer", color: empty ? "var(--portal-muted)" : undefined }}
                className={empty ? undefined : "report-row"}
              >
                <Td>{labelOf(r)}</Td>
                <Td align="right">{r.calls}</Td>
                <Td align="right">{fmtDuration(r.seconds)}</Td>
                <Td align="right">{r.answered > 0 ? fmtDuration(r.avgSeconds) : "—"}</Td>
                <Td align="right">
                  {r.nonAnswered}
                  {r.calls > 0 && <span style={{ color: "var(--portal-muted)", marginLeft: 6 }}>({Math.round((r.nonAnswered / r.calls) * 100)}%)</span>}
                </Td>
                <Td align="right">{!empty && <ChevronRight size={16} style={{ color: "var(--portal-muted)" }} />}</Td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 700, borderTop: "2px solid var(--portal-border)" }}>
            <Td>Total</Td>
            <Td align="right">{totals.calls}</Td>
            <Td align="right">{fmtDuration(totals.seconds)}</Td>
            <Td align="right">{totals.answered > 0 ? fmtDuration(totals.avgSeconds) : "—"}</Td>
            <Td align="right">
              {totals.nonAnswered}
              {totals.calls > 0 && <span style={{ color: "var(--portal-muted)", marginLeft: 6, fontWeight: 400 }}>({Math.round((totals.nonAnswered / totals.calls) * 100)}%)</span>}
            </Td>
            <Td />
          </tr>
        </tfoot>
      </table></ScrollShadow>
    </section>
  );
}

function sum(rows: ReportBucket[]): ReportBucket {
  const calls = rows.reduce((n, r) => n + r.calls, 0);
  const answered = rows.reduce((n, r) => n + r.answered, 0);
  const seconds = rows.reduce((n, r) => n + r.seconds, 0);
  return { key: "total", calls, answered, nonAnswered: calls - answered, seconds, avgSeconds: answered > 0 ? Math.floor(seconds / answered) : 0 };
}
